import type { ResearchFeed, ResearchTopic, ResearchYear } from "@kmitl/shared";
import { PanelHeader } from "./PanelHeader";

interface Props {
  feed: ResearchFeed | null;
  ageMinutes?: number | null;
  fallbackTier?: "live" | "unavailable" | "scenario" | "cache" | "database" | "reference";
  loading?: boolean;
}

/**
 * Research output — live from OpenAlex.
 *
 * This replaces a block of hand-typed Scopus totals (15,553 publications,
 * 151,882 citations) with a "publications by field" chart whose citation values
 * were zero for five of its six fields. Scopus is licensed and has no open API;
 * OpenAlex is CC0 and is queried live.
 *
 * TWO THINGS THIS PANEL REFUSES TO IMPLY
 * --------------------------------------
 * 1. **That recent years are final.** Citations accrue over years. OpenAlex
 *    reports 523 citations for 2026 against 13,335 for 2020 — a collapse that
 *    is purely an artefact of those papers being younger. The citation chart
 *    hatches the incomplete years and says so on the panel, because a bar
 *    chart that quietly drops 96% invites the exact wrong conclusion.
 * 2. **That the yearly series is the whole history.** The window is stated.
 *
 * Everything else is OpenAlex's own number, under OpenAlex's own definition.
 * h-index is not recomputed here.
 */
function Kpi({ value, label, sub }: { value: string; label: string; sub?: string }) {
  return (
    <div className="rsch-kpi">
      <span className="rsch-kpi-val mono">{value}</span>
      <span className="rsch-kpi-label mono">{label}</span>
      {sub ? <span className="rsch-kpi-sub mono">{sub}</span> : null}
    </div>
  );
}

/** Output and open access on one chart: bars for volume, a line for the share. */
function OutputChart({ years }: { years: ResearchYear[] }) {
  if (years.length < 2) return null;
  const W = 520, H = 130, PADL = 34, PADB = 20, PADT = 12;
  const innerW = W - PADL - 8;
  const maxWorks = Math.max(...years.map((y) => y.works));
  const bw = innerW / years.length;
  const yFor = (v: number) => PADT + (1 - v / maxWorks) * (H - PADT - PADB);

  // OA share on its own right-hand scale, so the two never share a misleading axis.
  const shares = years.map((y) => (y.works ? (y.openAccessWorks / y.works) * 100 : 0));
  const sharePts = shares.map((s, i) => `${PADL + bw * (i + 0.5)},${PADT + (1 - s / 100) * (H - PADT - PADB)}`);

  return (
    <figure className="rsch-figure">
      <svg viewBox={`0 0 ${W} ${H}`} className="rsch-chart" role="img"
        aria-label={`Annual research output ${years[0]!.year} to ${years[years.length - 1]!.year}, with open-access share rising from ${shares[0]!.toFixed(0)} to ${shares[shares.length - 1]!.toFixed(0)} percent`}>
        <line x1={PADL} y1={H - PADB} x2={W - 8} y2={H - PADB} className="rsch-axis" />
        {[0.5, 1].map((f) => (
          <g key={f}>
            <line x1={PADL} y1={yFor(maxWorks * f)} x2={W - 8} y2={yFor(maxWorks * f)} className="rsch-grid" />
            <text x={PADL - 5} y={yFor(maxWorks * f) + 3} className="rsch-tick rsch-tick--y">
              {Math.round(maxWorks * f).toLocaleString("en-GB")}
            </text>
          </g>
        ))}
        {years.map((y, i) => (
          <g key={y.year}>
            <rect
              x={PADL + bw * i + bw * 0.18} y={yFor(y.works)}
              width={bw * 0.64} height={Math.max(1, H - PADB - yFor(y.works))}
              className="rsch-bar"
            />
            {(i % Math.ceil(years.length / 7) === 0 || i === years.length - 1) && (
              <text x={PADL + bw * (i + 0.5)} y={H - 6} className="rsch-tick rsch-tick--x">{String(y.year).slice(2)}</text>
            )}
          </g>
        ))}
        <polyline points={sharePts.join(" ")} className="rsch-line" />
        {shares.map((s, i) => (
          <circle key={i} cx={PADL + bw * (i + 0.5)} cy={PADT + (1 - s / 100) * (H - PADT - PADB)} r="2" className="rsch-dot" />
        ))}
      </svg>
      <figcaption className="rsch-cap mono">
        <span className="rsch-key rsch-key--bar" /> works per year
        <span className="rsch-key rsch-key--line" /> open-access share, right axis 0–100%
      </figcaption>
    </figure>
  );
}

/** Citations per year, with the structurally incomplete years marked. */
function CitationChart({ years, incomplete }: { years: ResearchYear[]; incomplete: number[] }) {
  if (years.length < 2) return null;
  const W = 520, H = 96, PADL = 34, PADB = 18, PADT = 10;
  const innerW = W - PADL - 8;
  const max = Math.max(...years.map((y) => y.citations));
  const bw = innerW / years.length;
  const yFor = (v: number) => PADT + (1 - v / max) * (H - PADT - PADB);
  const cut = incomplete.length ? years.findIndex((y) => y.year === incomplete[0]!) : years.length;

  return (
    <figure className="rsch-figure">
      <svg viewBox={`0 0 ${W} ${H}`} className="rsch-chart" role="img"
        aria-label={`Citations received per publication year. The final ${incomplete.length} years are incomplete because citations accrue over time.`}>
        <line x1={PADL} y1={H - PADB} x2={W - 8} y2={H - PADB} className="rsch-axis" />
        <line x1={PADL} y1={yFor(max)} x2={W - 8} y2={yFor(max)} className="rsch-grid" />
        <text x={PADL - 5} y={yFor(max) + 3} className="rsch-tick rsch-tick--y">{max.toLocaleString("en-GB")}</text>
        {cut > 0 && (
          <line x1={PADL + bw * cut - bw * 0.1} y1={PADT} x2={PADL + bw * cut - bw * 0.1} y2={H - PADB} className="rsch-cut" />
        )}
        {years.map((y, i) => (
          <rect
            key={y.year}
            x={PADL + bw * i + bw * 0.18} y={yFor(y.citations)}
            width={bw * 0.64} height={Math.max(1, H - PADB - yFor(y.citations))}
            className={i >= cut ? "rsch-bar rsch-bar--partial" : "rsch-bar rsch-bar--cite"}
          />
        ))}
      </svg>
      <figcaption className="rsch-cap mono">
        <span className="rsch-key rsch-key--cite" /> citations received, per publication year
        {cut > 0 && <> <span className="rsch-key rsch-key--partial" /> {incomplete.length} most recent years still accumulating — not a decline</>}
      </figcaption>
    </figure>
  );
}

function Topics({ topics }: { topics: ResearchTopic[] }) {
  if (!topics.length) return null;
  const max = Math.max(...topics.map((t) => t.works ?? 0), 1);
  return (
    <div className="rsch-topics">
      {topics.slice(0, 8).map((t) => (
        <div className="rsch-topic" key={t.id || t.name}>
          <span className="rsch-topic-name">{t.name}</span>
          <span className="rsch-topic-meta mono">{t.field ?? t.domain ?? "—"}</span>
          <span className="rsch-topic-bar" aria-hidden>
            <span className="rsch-topic-fill" style={{ width: `${((t.works ?? 0) / max) * 100}%` }} />
          </span>
          <span className="rsch-topic-count mono">{t.works == null ? "—" : t.works.toLocaleString("en-GB")}</span>
        </div>
      ))}
    </div>
  );
}

export default function ResearchPanel({ feed, ageMinutes, fallbackTier, loading }: Props) {
  if (loading && !feed) {
    return (
      <section className="panel">
        <PanelHeader title="RESEARCH OUTPUT" source="openalex" />
        <p className="rsch-empty">Reading the OpenAlex record…</p>
      </section>
    );
  }

  if (!feed || feed.totalWorks == null) {
    return (
      <section className="panel">
        <PanelHeader title="RESEARCH OUTPUT" source="openalex" fallbackTier="unavailable" />
        <p className="rsch-empty">OpenAlex unavailable — no figures shown.</p>
        <p className="rsch-empty__note">
          Nothing is substituted when the source is unreachable. The previous static block is
          removed rather than reinstated.
        </p>
      </section>
    );
  }

  const first = feed.byYear[0]?.year;
  const last = feed.byYear[feed.byYear.length - 1]?.year;
  const oaFirst = feed.byYear[0]?.works ? (feed.byYear[0].openAccessWorks / feed.byYear[0].works) * 100 : null;
  const oaLast = feed.byYear[feed.byYear.length - 1]?.works
    ? (feed.byYear[feed.byYear.length - 1]!.openAccessWorks / feed.byYear[feed.byYear.length - 1]!.works) * 100
    : null;

  return (
    <section className="panel">
      <PanelHeader
        title="RESEARCH OUTPUT"
        source="openalex"
        ageMinutes={ageMinutes}
        fallbackTier={fallbackTier ?? "live"}
      />

      <div className="rsch-kpis">
        <Kpi value={feed.totalWorks.toLocaleString("en-GB")} label="WORKS INDEXED" />
        <Kpi value={feed.totalCitations?.toLocaleString("en-GB") ?? "—"} label="CITATIONS" />
        <Kpi value={feed.hIndex == null ? "—" : String(feed.hIndex)} label="H-INDEX" sub="OpenAlex definition" />
        <Kpi
          value={feed.twoYearMeanCitedness == null ? "—" : feed.twoYearMeanCitedness.toFixed(2)}
          label="MEAN CITEDNESS 2Y"
          sub="OpenAlex definition"
        />
      </div>

      {oaFirst != null && oaLast != null && (
        <p className="rsch-lead">
          Open access rose from <strong className="mono">{oaFirst.toFixed(0)}%</strong> in {first} to{" "}
          <strong className="mono">{oaLast.toFixed(0)}%</strong> in {last} of indexed output.
        </p>
      )}

      <OutputChart years={feed.byYear} />
      <CitationChart years={feed.byYear} incomplete={feed.incompleteCitationYears} />

      <span className="section-eyebrow mono rsch-h">SIGNATURE TOPICS</span>
      <Topics topics={feed.topics} />

      <p className="rsch-method" lang="en">{feed.methodEn}</p>
      <p className="rsch-method rsch-method--th" lang="th">{feed.methodTh}</p>
      <p className="rsch-foot mono">
        Institution{" "}
        <a href={feed.openAlexUrl} target="_blank" rel="noopener noreferrer">
          {feed.openAlexId}
        </a>
        {feed.ror ? ` · ROR ${feed.ror.replace("https://ror.org/", "")}` : ""} ·{" "}
        {first && last ? `${first}–${last} shown` : "yearly series unavailable"}
      </p>
    </section>
  );
}